import React, { useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslations } from 'next-intl';
import { Box } from 'models/Box';
import { AccessLevel } from 'models/AccessLevel';
import { faEllipsis } from '@fortawesome/free-solid-svg-icons';
import BoxTile from 'elements/BoxTile';
import ActionMenu, { ActionMenuEntry } from 'elements/ActionMenu';
import useAuthenticatedUser from 'networking/mindfeed/hooks/useAuthenticatedUser';
import classNames from 'classnames';
import { Space } from 'models/Space';
import { useSpaceNavigation } from 'xstate/hooks/useSpaceNavigation';
import { AvatarGroup, MediaAvatar } from 'elements/Avatar';
import { useBoxLabels } from 'hooks/useBoxLabels';
import { useBoxActions } from 'hooks/useBoxActions';

interface Props {
  space: Space;
  box: Box;
  isSelected: boolean;
  onSelect?: () => void;
  onEdit?: () => void;
}

const MemoryBox: React.FC<Props> = ({ box, isSelected, onSelect, onEdit }) => {
  const t = useTranslations();
  const { boxName } = useBoxLabels();
  const { goToBox } = useSpaceNavigation();

  // Details is always there, so every row has a menu.
  const actions = useBoxActions(box);

  const isShared = box.accessLevel === AccessLevel.Shared;
  const { authenticatedUser } = useAuthenticatedUser();
  // Members come with the box list, so a row never fetches on its own.
  const otherMembers = isShared
    ? (box.members ?? []).filter((m) => m.id !== authenticatedUser?.id)
    : [];
  const memoCount = box.stats?.memos.summary.totalCount;

  const onContainerClick = useCallback(() => {
    goToBox(box);
    onSelect?.();
  }, [box, onSelect, goToBox]);

  // The menu rows. Editing also closes the switcher the row may sit in.
  const menuItems: ActionMenuEntry[] = actions.map((action) => ({
    icon: action.outlineIcon,
    label: action.label,
    onClick: () => {
      action.onClick();
      // The edit form replaces whatever popover the row sits in.
      if (action.key === 'edit') {
        onEdit?.();
      }
    },
  }));

  return (
    // We use OnClick instead of a <Link /> attribute to prevent clicks on
    // MenuButton from triggering the parent link
    <div onClick={onContainerClick}>
      <div className={styles.container(isSelected)}>
        <BoxTile box={box} selected={isSelected} />

        <div className={styles.middle.container}>
          <p className={styles.middle.title}>{boxName(box.name)}</p>
          {/* Non-breaking space keeps the row height while counts load. */}
          <div className={styles.middle.subtitle}>
            {otherMembers.length > 0 && (
              <AvatarGroup
                component={MediaAvatar}
                size="3xs"
                max={3}
                ringClassName={
                  isSelected ? 'ring-surface-selection' : 'ring-surface-panel'
                }
                avatars={otherMembers.map((member) => ({
                  mediaId: member.profileImage?.id,
                  // One letter: two don't fit in 16px.
                  initials: (member.firstName || member.displayName || '?')
                    .trim()
                    .charAt(0),
                  colorKey: member.id,
                  alt: '',
                }))}
              />
            )}
            {otherMembers.length > 0 && (
              <span className="sr-only">
                {t('boxes.peopleCount', { count: otherMembers.length + 1 })}
              </span>
            )}
            <span>
              {memoCount !== undefined
                ? t('boxes.memoCount', { count: memoCount })
                : '\u00a0'}
            </span>
          </div>
        </div>

        <ActionMenu
          className={styles.right.container}
          triggerClassName={styles.right.btn.container(isSelected)}
          triggerAriaLabel={t('boxes.options')}
          stopPropagation
          anchor="bottom end"
          size="md"
          items={menuItems}
          trigger={() => (
            <FontAwesomeIcon
              icon={faEllipsis}
              className={styles.right.btn.icon}
              aria-hidden="true"
            />
          )}
        />
      </div>
    </div>
  );
};

const styles = {
  // Selection is a cool grey with a hairline edge, not a blue wash — the
  // row's badges carry the accent (matches the Swift apps).
  container: (isSelected: boolean) =>
    classNames(
      'group flex items-center gap-3 cursor-pointer select-none rounded-[10px] border px-2.5 py-2',
      isSelected
        ? 'border-surface-selection-border bg-surface-selection'
        : 'border-transparent hover:bg-ink-secondary/8',
    ),
  middle: {
    container: 'flex-1 min-w-0 space-y-1',
    title: 'text-sm font-semibold text-ink-primary truncate',
    subtitle: 'flex items-center gap-1.5 text-xs text-ink-secondary',
  },
  right: {
    container: 'relative',
    btn: {
      container: (isSelected: boolean) =>
        classNames(
          'inline-flex items-center rounded-md px-1 py-1.5 text-ink-secondary',
          'hover:bg-ink-secondary/10 focus:outline-hidden data-hover:bg-ink-secondary/10 data-open:bg-ink-secondary/10',
          isSelected && 'visible lg:invisible lg:group-hover:visible',
          !isSelected && 'invisible lg:group-hover:visible',
        ),
      icon: 'fa-fw text-xs',
    },
  },
};

export default MemoryBox;
